// Substitution flow utilities. The actual DDS write + MQ publish for a
// sub now goes through eventService.trigger({ type: 'sub', ... }) — the
// orchestrator hands the payload to sub_event_object_builder, which is
// the single source of truth for the event shape (game_period stringify,
// NLE graphics, MQ topic). shirt_number_off/on added (OPUS-1339).
// The legacy direct-DDS path (sendLiveEvent)
// is gone; the validator in @aelive/dds-client rejects numeric
// game_period and the plugin handles that conversion centrally.

import { shouldShowGoalkeeperIcon } from '@ae/lineup';
import { getEventName, playerKeys } from './playerName';

const shirtNumber = (p) => {
    const n = parseInt(p?.shirt_number, 10);
    return Number.isNaN(n) ? Infinity : n;
};

// Substitution picker order: goalkeepers first (same glove rule as the lineup
// panel), then by shirt number. `listType` is 'lineup' or 'subs'.
export const sortForSubstitution = (players = [], listType) => {
    const gk = (p) => (shouldShowGoalkeeperIcon(p, listType) ? 0 : 1);
    return [...players].sort((a, b) => gk(a) - gk(b) || shirtNumber(a) - shirtNumber(b));
};

// Both lists of a team's lineup, sorted for the substitution picker.
export const sortLineupForSubstitution = (lineup) =>
    lineup && {
        ...lineup,
        players: sortForSubstitution(lineup.players, 'lineup'),
        subs: sortForSubstitution(lineup.subs, 'subs'),
    };

export const findPlayerByShirtNumber = (team, shirtNumber) => {
  if (!team || !shirtNumber) return null;

  const playerMap = new Map(
    (team.player || []).map((p) => [p.player_id, p])
  );

  const player = Array.from(playerMap.values()).find(
    (p) => String(p.shirt_number) === String(shirtNumber)
  );

  if (!player) return null;

  const lineupItem = (team.lineup || []).find(
    (l) => l.lineup_id === player.player_id
  );

  if (!lineupItem) return null;
  // Some subscription payloads create lineup entries without role in data.
  // If lineup_id maps to a known player, treat missing role as Player.
  const lineupRole = (typeof lineupItem.role === 'string' && lineupItem.role.trim().toLowerCase() === 'player')
    ? 'Player'
    : (lineupItem.role || 'Player');
  if (lineupRole !== "Player") return null;

  return {
    player_id: player.player_id,
    lineup_id: lineupItem.lineup_id,
    shirt_number: player.shirt_number,
    formation_place: lineupItem.formation_place,
    player_position: player.player_position,
    player: player,
    lineup: lineupItem,
  };
};
// The players of a multi-pair substitution, one { off, on } entry per pair in
// the order they will be logged. Every pair is sent with the whole group, and
// the sub plugin records a single NLE clip from it on the last pair. Names come
// from getEventName, as in sendSubstitutionPatches, so the clip shows the same
// names as the events. Throws if any player can't be found, so a bad shirt
// number stops the group before any pair is sent.
const subGroupPlayer = (player) => ({
  strShirtNumber: String(player?.shirt_number ?? ''),
  strSecondName: getEventName(player) || '',
});

export const buildSubGroup = (team, shirtPairs) =>
  shirtPairs.map(({ off, on }) => {
    const playerOff = findPlayerByShirtNumber(team, off);
    const playerOn = findPlayerByShirtNumber(team, on);
    if (!playerOff || !playerOn) {
      throw new Error(`Could not find one or both players by shirt number (${off} off, ${on} on)`);
    }
    return { off: subGroupPlayer(playerOff.player), on: subGroupPlayer(playerOn.player) };
  });

const SUB_EVENT_KEYS = new Set(['sub', 'concussion_sub']);

// Rows are logged in event_id order: the plugin mints `<event_key>_<Date.now()>`.
const loggedAt = (row) => {
  const ms = Number(/(\d+)$/.exec(row?.event_id ?? '')?.[1]);
  return Number.isFinite(ms) ? ms : Infinity;
};

// The group an edited sub belongs to, as { index, players }, or null when it
// stands alone. The DDS rows don't record which subs went on together, but the
// dialog logs every pair of a multi-pair substitution with one clock time — so
// the group is the subs of the same team, period and clock second as the
// edited row AFTER the edit, in the order they were logged. `edited` carries
// the edit applied (event_id, team_id, time_in_seconds, game_period,
// player1_id off, player2_id on); `playerLookup` maps player_id to player.
export const subGroupForEdit = (fixtureEvents, edited, playerLookup) => {
  const sameMoment = (row) =>
    SUB_EVENT_KEYS.has(row?.event_key) &&
    row.event_id !== edited.event_id &&
    row.team_id === edited.team_id &&
    Number(row.time_in_seconds) === Number(edited.time_in_seconds) &&
    String(row.game_period) === String(edited.game_period);

  const siblings = (fixtureEvents || []).filter(sameMoment);
  if (siblings.length === 0) return null;

  const rows = [...siblings, edited].sort((a, b) => loggedAt(a) - loggedAt(b));
  return {
    index: rows.indexOf(edited),
    players: rows.map((row) => ({
      off: subGroupPlayer(playerLookup?.get(row.player1_id)),
      on: subGroupPlayer(playerLookup?.get(row.player2_id)),
    })),
  };
};

// Log one substitution through the event-service plugin path. Looks up
// the off/on lineup_ids from shirt numbers, then fires the sub plugin
// via eventService.trigger so the orchestrator owns the DDS write + MQ
// publish + (optional) NLE replay. The plugin stringifies game_period
// and mints a fresh event_id internally. `subGroup` ({ index, players }) marks
// this pair as part of a multi-pair substitution — see buildSubGroup.
export const sendSubstitutionPatches = async ({
  eventService,
  playerOffShirtNumber,
  playerOnShirtNumber,
  team,
  teamSide = null,
  competitionId,
  seasonId,
  fixtureId,
  timeInSeconds = 0,
  gamePeriod = null,
  renderer = null,
  profileId = null,
  userRole = null,
  isConcussion = false,
  subGroup = null,
}) => {
  if (!eventService?.trigger) {
    throw new Error('eventService.trigger is required to log a substitution');
  }
  if (!team || !playerOffShirtNumber || !playerOnShirtNumber) {
    throw new Error('Missing required data for substitution');
  }

  if (!competitionId || !seasonId || !fixtureId) {
    throw new Error('Missing required IDs for substitution');
  }

  const playerOff = findPlayerByShirtNumber(team, playerOffShirtNumber);
  const playerOn = findPlayerByShirtNumber(team, playerOnShirtNumber);

  if (!playerOff || !playerOn) {
    throw new Error('Could not find one or both players by shirt number');
  }

  const playerOffFormationPlace = playerOff.formation_place || '';
  const playerOffPosition = playerOff.player_position || '';
  // The sub graphic's strSecondNameOff/On are SURNAME fields, so resolve them
  // through getEventName — the same event-context chain every other event path
  // uses (unique_name -> player_short_last_name -> player_last_name -> ...).
  // The old chain checked player_short_name FIRST, which is a given name, so a
  // populated one put a first name in a surname slot, and a player carrying
  // only unique_name / player_short_last_name resolved to nothing at all.
  const playerOffName = getEventName(playerOff.player) || null;
  const playerOnName = getEventName(playerOn.player) || null;

  return eventService.trigger({
    type: 'sub',
    event_op: 'create',
    renderer,
    fixtureId,
    competitionId,
    seasonId,
    team_id: team.team_id,
    team_side: teamSide,
    player1_id: playerOff.lineup_id,
    player2_id: playerOn.lineup_id,
    player_off_name: playerOffName,
    player_on_name: playerOnName,
    shirt_number_off: playerOff.shirt_number ?? null,
    shirt_number_on: playerOn.shirt_number ?? null,
    ...playerKeys(playerOff.player, 'Off'),
    ...playerKeys(playerOn.player, 'On'),
    player_off_position: playerOffPosition,
    player_off_place: playerOffFormationPlace,
    time_in_seconds: Number(timeInSeconds) || 0,
    game_period: gamePeriod,
    is_concussion: Boolean(isConcussion),
    ...(subGroup ? { sub_group: subGroup } : {}),
    profile_id: profileId,
    user_role: userRole,
  });
};

export const parseShirtNumbers = (input) => {
  if (!input || typeof input !== 'string') return null;

  const numbers = input.trim().split(/\s+/);
  if (numbers.length !== 2) return null;

  const [playerOff, playerOn] = numbers;
  if (!playerOff || !playerOn) return null;

  return {
    playerOffShirtNumber: playerOff.trim(),
    playerOnShirtNumber: playerOn.trim(),
  };
};
